<template>
  <div v-loading="isLoading">
    <el-table
      :data="carriersettings"
      :default-sort="{prop: 'carrier.name', order: 'ascending'}"
      stripe
    >
      <el-table-column type="expand">
        <template slot-scope="props">
          <CarrierServicesTable
            :account-id="accountId"
            :carrier-id="props.row.carrier.id"
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Carrier"
        sortable
        :sort-method="sortCarrier"
        prop="carrier.name"
        min-width="320px"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          <p>
            {{ scope.row.carrier ? (scope.row.carrier.name || '-') : '-' }}&nbsp;<el-tag
              v-if="scope.row.carrier_is_customers"
              type="warning"
              size="small"
            >
              Own Account
            </el-tag>
          </p>
        </template>
      </el-table-column>

      <el-table-column
        label="Charge Account"
        prop="charge_account"
        width="180px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue || '-'"
      />

      <el-table-column
        label="EDI Sys ID"
        prop="edi_sysid"
        width="180px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue || '-'"
      />

      <el-table-column
        label="Enabled"
        align="center"
        width="120px"
      >
        <template slot-scope="scope">
          <font-awesome-icon
            v-if="scope.row.active_in_shipper"
            :icon="['fal', 'check-circle']"
            size="2x"
            style="color: #1dc457;"
          />
          <font-awesome-icon
            v-else
            :icon="['fal', 'times-circle']"
            size="2x"
            style="color: red;"
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Active"
        align="center"
        width="120px"
      >
        <template slot-scope="scope">
          <font-awesome-icon
            v-if="scope.row.carrier_active"
            :icon="['fal', 'check-circle']"
            size="2x"
            style="color: #1dc457;"
          />
          <font-awesome-icon
            v-else
            :icon="['fal', 'times-circle']"
            size="2x"
            style="color: red;"
          />
        </template>
      </el-table-column>

      <el-table-column
        v-if="canUpdate || canDelete"
        label="Actions"
        align="right"
        width="270px"
      >
        <template slot-scope="scope">
          <el-button
            v-if="canUpdate"
            size="mini"
            @click.native="editCarrierSetting(scope.$index, scope.row)"
          >
            Edit
          </el-button>
          <el-button
            v-if="canDelete"
            size="mini"
            type="danger"
            @click.native="deleteCarrierSetting(scope.$index, scope.row)"
          >
            Delete
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <!-- Pagination -->
    <br>
    <div style="text-align: center;">
      <el-pagination
        :current-page.sync="page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size.sync="perPage"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @size-change="loadTable()"
        @current-change="loadTable()"
      />
    </div>
    <!-- Bulk Create Form -->
    <CarrierSettingsBulkCreateForm
      ref="bulkCreateForm"
      :account-id="accountId"
      @created="loadTable"
    />
    <!-- Edit Form Modal -->
    <CarrierSettingEditModal
      ref="editModal"
      :account-id="accountId"
      @saved="loadTable"
    />
  </div>
</template>

<script>
import CarrierServicesTable from '@/components/Application/Manage/Account/CarrierServicesTable'
import CarrierSettingsBulkCreateForm from '@/components/Application/Manage/Account/CarrierSettingsBulkCreateForm'
import CarrierSettingEditModal from '@/components/Application/Manage/Account/CarrierSettingEditModal'
import { CREATE, UPDATE, DELETE } from '@/plugins/user'

export default {
  components: {
    CarrierServicesTable,
    CarrierSettingsBulkCreateForm,
    CarrierSettingEditModal
  },
  props: {
    accountId: {
      type: String,
      required: true
    },
    filters: {
      type: Array,
      required: false,
      default: () => { return [] }
    },
    sort: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      carriersettings: [],
      page: 1,
      perPage: 50,
      total: 0,
      isLoading: true
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, 'account_carriersetting')
    },
    canDelete () {
      return this.$user.can(DELETE, 'account_carriersetting')
    }
  },
  mounted () {
    this.actions = [
      {
        hidden: this.$user.cannot(CREATE, 'account_carriersetting'),
        name: 'Bulk Create',
        icon: ['fal', 'plus'],
        event: () => {
          this.$refs.bulkCreateForm.isShow = true
        }
      },
      {
        name: 'Refresh',
        icon: ['fal', 'sync'],
        event: () => this.loadTable()
      }
    ]
  },
  methods: {
    sortCarrier (a, b) {
      return a.carrier.name.localeCompare(b.carrier.name)
    },
    loadTable () {
      this.isLoading = true
      this.$sdk.account_carriersetting.list(builder => {
        builder.context = { resource: 'account', id: this.accountId }
        builder.include = ['carrier']
        builder.filters = this.filters
        builder.page = this.page
        builder.limit = this.perPage
        builder.order = `${this.sort.field}:${this.sort.direction}`
      })
        .then(res => {
          this.carriersettings = res
          this.total = res.total
        })
        .finally(() => { this.isLoading = false })
    },
    editCarrierSetting (index, row) {
      this.$refs.editModal.carrierSetting = row
      this.$refs.editModal.isShow = true
    },
    deleteCarrierSetting (index, row) {
      this.$confirm('Are you sure to delete this carrier setting?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.isLoading = true
        row.delete(builder => {
          builder.context = { resource: 'account', id: this.accountId }
        })
          .then(() => this.$message({ message: 'Successfully deleted', type: 'success' }))
          .finally(() => this.loadTable())
      }).catch(() => {})
    }
  }
}
</script>

<style>

</style>
